/**
 * Ground ring flashes at both tiers (integration plan slice 2b): a passive
 * that fires shows a ring widening under its unit, a charm that shatters a
 * wider burst. One instanced draw of flat rings, additive and depth-read-only
 * so the ground never fights it; drawn only while a ring plays (no draw when
 * idle). A ring is an effect, not a unit or a sprite, so it grows by scale.
 * No particles, no lights (ADR 0028's dungeon rule).
 */
import * as THREE from 'three';
import { presentation } from './constants.ts';

type Ring = { x: number; y: number; to: number; t: number; readonly color: THREE.Color };

export type Effects = {
  readonly mesh: THREE.InstancedMesh;
  /** Plays one ring at game tile coordinates; `step(t)` is driven by the caller's clock. */
  ring(x: number, y: number, color: THREE.Color, burst: boolean): Ring;
  finish(ring: Ring): void;
  update(elevation: (x: number, y: number) => number): void;
  dispose(): void;
};

const r = presentation.ring;

export const createEffects = (scene: THREE.Scene): Effects => {
  const geometry = new THREE.RingGeometry(r.inner, 1, r.segments).rotateX(-Math.PI / 2);
  const material = new THREE.MeshBasicMaterial({ color: 0xffffff, blending: THREE.AdditiveBlending, transparent: true, depthWrite: false, toneMapped: false });
  const mesh = new THREE.InstancedMesh(geometry, material, r.capacity);
  mesh.name = 'effect-rings';
  mesh.frustumCulled = false;
  mesh.visible = false;
  mesh.setColorAt(0, new THREE.Color(0, 0, 0));
  scene.add(mesh);
  const rings: Ring[] = [];
  const m = new THREE.Matrix4();
  const c = new THREE.Color();
  return {
    mesh,
    ring(x, y, color, burst) {
      const ring = { x, y, to: burst ? r.burstTo : r.to, t: 0, color };
      if (rings.length >= r.capacity) rings.shift();
      rings.push(ring);
      return ring;
    },
    finish(ring) {
      const i = rings.indexOf(ring);
      if (i >= 0) rings.splice(i, 1);
    },
    update(elevation) {
      mesh.visible = rings.length > 0;
      rings.forEach((g, i) => {
        const radius = r.from + (g.to - r.from) * g.t;
        m.makeTranslation(g.x, elevation(g.x, g.y) + r.y, -g.y).multiply(new THREE.Matrix4().makeScale(radius, 1, radius));
        mesh.setMatrixAt(i, m);
        mesh.setColorAt(i, c.copy(g.color).multiplyScalar(1 - g.t));
      });
      mesh.count = rings.length;
      mesh.instanceMatrix.needsUpdate = true;
      if (mesh.instanceColor) mesh.instanceColor.needsUpdate = true;
    },
    dispose() {
      mesh.removeFromParent();
      mesh.dispose();
      geometry.dispose();
      material.dispose();
    },
  };
};
